import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data-test-id", "editorToolbarStartSlot", "editorToolbarEndSlot", "Heading", "titleText", "tabsStartSlot", "tabsMiddleSlot", "tabsEndSlot", "tabsStyles", "toolbarStyles"],
  _excluded2 = ["editorToolbarStartSlot", "editorToolbarEndSlot", "Heading", "tabsStartSlot", "tabsMiddleSlot", "tabsEndSlot", "titleText", "shouldAdaptForBranchPreview", "data-test-id"];
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useEffect } from 'react';
import UIEditorToolbar from 'UIComponents/nav/UIEditorToolbar';
import UITabBar from 'UIComponents/nav/UITabBar';

// @ts-expect-error not migrated yet
import { currentProjectVersion } from 'hubspot.bender';
import { EditorToolbar } from 'foundations-components/transitional/components/EditorToolbar';
import { TabBar } from 'foundations-components/transitional/components/TabBar';
import { withNonTrellisComponent, withTrellisComponent } from '../hoc/withTrellisComponentHOCs';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { filterProps } from 'foundations-components/utils/filterProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TrellisEPHeaderWrapper = styled.div.withConfig({
  displayName: "EPHeader__TrellisEPHeaderWrapper",
  componentId: "sc-81qp05-0"
})(({
  $cssStyles,
  theme: _theme
}) => {
  const theme = _theme;
  // Super gross to target the internals of `UITabBar` and `UIEditorToolbar`, especially using the names of components, but this was
  // recommended to us by Trellis folks: https://hubspot.slack.com/archives/C1WG3L3J6/p1740582560511349?thread_ts=1740518549.515709&cid=C1WG3L3J6

  return Object.assign({}, $cssStyles, {
    /**
     * Targetting the following structure:
     * <div class="EditorToolbar"> - Toolbar
     *  <div class="StyledFlex" role="group"> - Flex Wrapper
     *   <div class="StyledFlex"> - Another Flex wrapper
     *    <div class="StyledFlex"> - Slot
     *    <div class="StyledFlex"> - Slot
     *    <div class="StyledFlex"> - Slot
     *      <button> - Button that is immediate child of slot
     */
    '[class*="StyledFlex"][role="group"] > [class*="StyledFlex"]:first-of-type > [class*="StyledFlex"]': {
      flexBasis: '33.3%',
      paddingBlock: 0,
      paddingTop: 0,
      paddingBottom: 0,
      // Targets buttons as immediate children in flex slots in both TabBar and EditorToolbar
      // Similar to https://git.hubteam.com/HubSpot/UIComponents/blob/f8a0f921b6d718c143fd06142cbe9c8080444a0e/UIComponents/static-3.5815/js/nav/UITabBar.tsx#L25-L29
      '> [class*="StyledButton"]': {
        flex: '0 0 auto'
      }
    },
    '[class*="EditorToolbar"] > [role="group"]': {
      paddingInlineStart: theme.space['500'],
      // Targets buttons as immediate children in flex slots in EditorToolbar, overriding styles above
      // Matches https://git.hubteam.com/HubSpot/UIComponents/blob/f8a0f921b6d718c143fd06142cbe9c8080444a0e/UIComponents/static-3.5815/js/nav/AbstractToolbar.tsx#L64-L69
      ' > [class*="StyledFlex"]:first-of-type > [class*="StyledFlex"] > [class*="StyledButton"]': {
        flexShrink: 0,
        marginTop: 0,
        width: 'auto'
      }
    }
  });
});
function TrellisEPHeaderComponent(_ref) {
  let {
      'data-test-id': dataTestId,
      editorToolbarStartSlot,
      editorToolbarEndSlot,
      Heading,
      titleText,
      tabsStartSlot,
      tabsMiddleSlot,
      tabsEndSlot,
      tabsStyles,
      toolbarStyles
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const hasTabs = tabsStartSlot || tabsMiddleSlot || tabsEndSlot;
  const {
    tokenProps
  } = filterProps(rest);
  const wrapperStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsxs(TrellisEPHeaderWrapper, {
    "data-test-id": dataTestId,
    $cssStyles: wrapperStyles,
    children: [/*#__PURE__*/_jsx(EditorToolbar, Object.assign({
      startSlot: editorToolbarStartSlot,
      endSlot: editorToolbarEndSlot,
      Heading: Heading,
      titleText: titleText,
      "data-test-id": "ep-editor-toolbar"
    }, toolbarStyles)), hasTabs && /*#__PURE__*/_jsx(TabBar, Object.assign({
      startSlot: tabsStartSlot,
      middleSlot: tabsMiddleSlot,
      endSlot: tabsEndSlot,
      paddingBlockStart: "0",
      paddingInlineStart: "200",
      marginBlock: "200"
    }, tabsStyles))]
  });
}
const HeaderWrapper = styled.div.withConfig({
  displayName: "EPHeader__HeaderWrapper",
  componentId: "sc-81qp05-1"
})(["div[class*='ToolbarInner'] > *{padding:0;}div[class*='SlotFlexContainer']{flex-basis:33%;}div[class*='SlotFlexContainer'] *{flex-grow:0;}div[class*='UIEditorToolbar__EditorToolbar']{padding-left:22px;}div[class*='UITabBar__TabBar']{padding-left:2px;}"]);
const EPHeader = _ref2 => {
  let {
      editorToolbarStartSlot,
      editorToolbarEndSlot,
      Heading,
      tabsStartSlot,
      tabsMiddleSlot,
      tabsEndSlot,
      titleText,
      shouldAdaptForBranchPreview,
      'data-test-id': dataTestId = 'ep-editor-header'
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const hasTabs = tabsStartSlot || tabsMiddleSlot || tabsEndSlot;
  useEffect(() => {
    // Show alternate header styling for branch previews
    if (shouldAdaptForBranchPreview && currentProjectVersion.match(/^static-\d+-[^.]+\.\d+$/)) {
      const headerElement = document.querySelector(`[data-test-id="ep-editor-toolbar"]`);
      if (headerElement) {
        headerElement.classList.add('hubspot-branch-preview');
      }
    }
  }, [shouldAdaptForBranchPreview]);

  // Putting `isTrellisCompatibleComponent` in `...rest` here so TS can type narrow the rest of the props
  if (rest.isTrellisCompatibleComponent) {
    return /*#__PURE__*/_jsx(TrellisEPHeaderComponent, Object.assign({
      editorToolbarStartSlot: editorToolbarStartSlot,
      editorToolbarEndSlot: editorToolbarEndSlot,
      Heading: Heading,
      tabsStartSlot: tabsStartSlot,
      tabsMiddleSlot: tabsMiddleSlot,
      tabsEndSlot: tabsEndSlot,
      titleText: titleText,
      shouldAdaptForBranchPreview: shouldAdaptForBranchPreview,
      "data-test-id": dataTestId
    }, rest));
  }
  const {
    className,
    editorToolbarClassName,
    tabsClassName = 'm-y-3'
  } = rest;
  return /*#__PURE__*/_jsxs(HeaderWrapper, {
    className: className,
    "data-test-id": dataTestId,
    children: [/*#__PURE__*/_jsx(UIEditorToolbar
    // TODO: Migrate usage in https://git.hubteam.com/HubSpot/ContentEditorUI/blob/b864c9e1c31535429804c622e3caab179877c721/selenium/src/utils/PageComponents.js#L453
    , {
      className: editorToolbarClassName,
      startSlot: editorToolbarStartSlot,
      endSlot: editorToolbarEndSlot,
      Heading: Heading,
      titleText: titleText,
      "data-test-id": "ep-editor-toolbar"
    }), hasTabs && /*#__PURE__*/_jsx(UITabBar, {
      className: tabsClassName,
      startSlot: tabsStartSlot,
      middleSlot: tabsMiddleSlot,
      endSlot: tabsEndSlot
    })]
  });
};
EPHeader.propTypes = {
  className: PropTypes.string,
  Heading: PropTypes.node,
  editorClassName: PropTypes.string,
  editorStartSlot: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  editorEndSlot: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  tabsClassName: PropTypes.string,
  tabsStartSlot: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  tabsMiddleSlot: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  tabsEndSlot: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  titleText: PropTypes.node,
  shouldAdaptForBranchPreview: PropTypes.bool
};
export const LegacyEpHeader =
// @ts-expect-error prop types not playing nicely with the HOC
withNonTrellisComponent()(EPHeader);
export const TrellisEPHeader =
// @ts-expect-error prop types not playing nicely with the HOC
withTrellisComponent()(EPHeader);
export default LegacyEpHeader;